import {
	Component,
	ElementRef,
	EventEmitter,
	Input,
	OnInit,
	Output,
	SimpleChanges,
	ViewChild,
} from '@angular/core';

import { Subject } from 'rxjs';
import { debounceTime, finalize, switchMap, takeUntil, tap } from 'rxjs/operators';

import {
	C4ListResponse,
	IS_NOT_EMPTY,
	IS_NOT_NULL_OR_UNDEFINED,
	IS_NULL_OR_UNDEFINED,
	RestService,
} from 'cargo-chief-commons';

import { UrlService } from '@service/url/url.service';
import { UtilService } from '@service/util/util.service';
import { MtxPopoverTrigger } from '@ng-matero/extensions/popover';
import { BaseComponent } from '@shared/base/base.component';

@Component({
	selector: 'app-network-accounts-popover',
	templateUrl: './network-accounts-popover.component.html',
	styleUrls: ['./network-accounts-popover.component.scss'],
})
export class NetworkAccountsPopoverComponent extends BaseComponent implements OnInit {
	@Input() carrierId: string = null;
	@Input() accountSelected: string = null;
	@Input() placeholder: string = 'Business Name';
	@Input() customFormFieldClass: string = 'custom-form-field';
	@Input() showCloseIcon: boolean = false;

	@Output() setAccount = new EventEmitter<any>();

	accountsList: Array<any> = [];
	isLoadingAccountsList: boolean = false;
	searchText: string = '';

	debouncer: Subject<void> = new Subject<void>();
	@ViewChild(MtxPopoverTrigger) trigger: MtxPopoverTrigger;
	@ViewChild('searchInput') searchInput: ElementRef<HTMLInputElement>;

	IS_NOT_EMPTY = IS_NOT_EMPTY;

	constructor(
		public utilService: UtilService,
		private urlService: UrlService,
		private restService: RestService
	) {
		super();
	}

	ngOnInit() {
		if (IS_NOT_EMPTY(this.accountSelected)) {
			this.placeholder = this.accountSelected;
		}

		this.debouncer
			.pipe(
				debounceTime(500),
				tap(() => {
					this.isLoadingAccountsList = true;
					this.accountsList = [];
				}),
				switchMap(() => {
					const url = this.urlService.networkAccounts(0, 5);
					const payload = {
						carrierId: this.carrierId,
						businessNameQuery: this.searchText,
					};
					return this.restService.POST(url, payload).pipe(
						takeUntil(this.isDestroyed$),
						finalize(() => {
							this.isLoadingAccountsList = false;
						})
					);
				})
			)
			.subscribe((response: C4ListResponse<ListNetworkAccountsResponse>) => {
				this.accountsList = response.data;
			});
	}

	ngOnChanges(changes: SimpleChanges) {
		if (IS_NULL_OR_UNDEFINED(this.accountSelected)) {
			this.placeholder = 'Business Name';
		} else {
			this.placeholder = this.accountSelected;
		}
	}

	inputChanged(searchedText: string) {
		if (searchedText.length > 0) {
			this.debouncer.next();
		} else {
			this.loadInitialAccountsList();
		}
	}

	loadInitialAccountsList() {
		this.isLoadingAccountsList = true;
		const url = this.urlService.networkAccounts(0, 5);
		const payload = {
			carrierId: this.carrierId,
		};
		this.restService
			.POST(url, payload)
			.pipe(
				takeUntil(this.isDestroyed$),
				finalize(() => {
					this.isLoadingAccountsList = false;
				})
			)
			.subscribe((response: C4ListResponse<ListNetworkAccountsResponse>) => {
				this.accountsList = response.data;
			});
	}

	setSelectedAccount(account: ListNetworkAccountsResponse) {
		this.accountSelected = account.businessName;
		this.updatePlaceholderAndClose();
	}

	updatePlaceholderAndClose() {
		this.placeholder = this.accountSelected;
		this.setAccount.emit(this.accountSelected);
		if (IS_NOT_NULL_OR_UNDEFINED(this.trigger)) {
			this.trigger.closePopover();
		}
	}

	renderDownArrowIcon(): boolean {
		if (!this.showCloseIcon || (this.showCloseIcon && IS_NULL_OR_UNDEFINED(this.accountSelected))) {
			return true;
		}
		return false;
	}

	renderCloseArrowIcon(): boolean {
		if (this.showCloseIcon && IS_NOT_NULL_OR_UNDEFINED(this.accountSelected)) {
			return true;
		}
		return false;
	}

	removeSelectedAccount() {
		this.accountSelected = null;
		this.setAccount.emit(null);
		this.placeholder = 'Business Name';
	}

	focusToSearchInput() {
		setTimeout(() => {
			if (IS_NOT_NULL_OR_UNDEFINED(this.searchInput)) {
				this.searchInput.nativeElement.focus();
			}
		});
	}
}

interface ListNetworkAccountsResponse {
	businessName: string;
}
